import {
  CognitoUserPool,
  CognitoUser,
  AuthenticationDetails,
  CognitoUserSession,
  CognitoIdToken,
  ICognitoStorage,
} from 'amazon-cognito-identity-js';
import { config } from './config';
import { clearLocalSession, getCurrentLocalSession } from './localAuth';

const PERSISTENT_FLAG = 'bs.persistent';
const COGNITO_PREFIX = 'CognitoIdentityServiceProvider.';

const SwitchableStorage: ICognitoStorage = {
  setItem(key, value) {
    if (localStorage.getItem(PERSISTENT_FLAG) === '1') {
      localStorage.setItem(key, value);
    } else {
      sessionStorage.setItem(key, value);
    }
  },
  getItem(key) {
    return sessionStorage.getItem(key) ?? localStorage.getItem(key);
  },
  removeItem(key) {
    localStorage.removeItem(key);
    sessionStorage.removeItem(key);
  },
  // Cognito roept clear() aan bij signOut. Een kale localStorage.clear() nam
  // ook onze eigen bs.-sleutels mee (actief profiel, paginagrootte, laatst
  // geopende signaal), dus uitloggen wiste voorkeuren die daar los van staan.
  clear() {
    for (const storage of [localStorage, sessionStorage]) {
      // Eerst verzamelen, dan verwijderen: bij removeItem tijdens het lopen
      // schuiven de indexen op en sla je sleutels over.
      const keys: string[] = [];
      for (let index = 0; index < storage.length; index++) {
        const key = storage.key(index);
        if (key?.startsWith(COGNITO_PREFIX)) {
          keys.push(key);
        }
      }
      keys.forEach((key) => storage.removeItem(key));
    }
  },
};

function setRememberMe(remember: boolean): void {
  if (remember) {
    localStorage.setItem(PERSISTENT_FLAG, '1');
  } else {
    localStorage.removeItem(PERSISTENT_FLAG);
  }
}

let userPool: CognitoUserPool | null = null;
function getPool() {
  if (!userPool && config.userPoolId && config.userPoolClientId) {
    userPool = new CognitoUserPool({
      UserPoolId: config.userPoolId,
      ClientId: config.userPoolClientId,
      Storage: SwitchableStorage,
    });
  }
  return userPool;
}

function newCognitoUser(email: string): CognitoUser | null {
  const pool = getPool();
  if (!pool) {
    return null;
  }
  return new CognitoUser({ Username: email, Pool: pool, Storage: SwitchableStorage });
}

export type Role = 'backoffice' | 'customer';

export interface SessionInfo {
  email: string;
  sub: string;
  role: Role;
  idToken: string;
  localPersona?: string;
}

export type LoginResult =
  | { kind: 'session'; session: SessionInfo }
  | { kind: 'newPasswordRequired'; complete: (newPassword: string) => Promise<SessionInfo> };

function sessionFromCognito(session: CognitoUserSession): SessionInfo {
  const idToken: CognitoIdToken = session.getIdToken();
  const payload = idToken.payload as Record<string, unknown>;
  const groups = (payload['cognito:groups'] as string[] | undefined) ?? [];
  const role: Role = groups.includes('backoffice') ? 'backoffice' : 'customer';
  return {
    email: String(payload.email ?? ''),
    sub: String(payload.sub ?? ''),
    role,
    idToken: idToken.getJwtToken(),
  };
}

export function getCurrentSession(): Promise<SessionInfo | null> {
  if (config.localAuth) {
    return Promise.resolve(getCurrentLocalSession());
  }
  return new Promise((resolve) => {
    const pool = getPool();
    if (!pool) {
      return resolve(null);
    }
    const user = pool.getCurrentUser();
    if (!user) {
      return resolve(null);
    }
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) {
        return resolve(null);
      }
      resolve(sessionFromCognito(session));
    });
  });
}

export function login(email: string, password: string, remember: boolean): Promise<LoginResult> {
  if (config.localAuth) {
    return Promise.reject(new Error('Lokale modus. Gebruik de persona-dropdown in plaats van login.'));
  }
  setRememberMe(remember);
  return new Promise((resolve, reject) => {
    const user = newCognitoUser(email);
    if (!user) {
      return reject(new Error('Cognito niet geconfigureerd'));
    }
    const auth = new AuthenticationDetails({ Username: email, Password: password });
    user.authenticateUser(auth, {
      onSuccess: (session) => resolve({ kind: 'session', session: sessionFromCognito(session) }),
      onFailure: (err) => reject(err),
      newPasswordRequired: () => {
        const complete = (newPassword: string) =>
          new Promise<SessionInfo>((response, rej) => {
            user.completeNewPasswordChallenge(
              newPassword,
              {},
              {
                onSuccess: (session) => response(sessionFromCognito(session)),
                onFailure: (err) => rej(err),
              },
            );
          });
        resolve({ kind: 'newPasswordRequired', complete });
      },
    });
  });
}

export function requestPasswordReset(email: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = newCognitoUser(email);
    if (!user) {
      return reject(new Error('Cognito niet geconfigureerd'));
    }
    user.forgotPassword({
      onSuccess: () => resolve(),
      inputVerificationCode: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function confirmPasswordReset(email: string, code: string, newPassword: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const user = newCognitoUser(email);
    if (!user) {
      return reject(new Error('Cognito niet geconfigureerd'));
    }
    user.confirmPassword(code, newPassword, {
      onSuccess: () => resolve(),
      onFailure: (err) => reject(err),
    });
  });
}

export function logout(): void {
  if (config.localAuth) {
    clearLocalSession();
    return;
  }
  const pool = getPool();
  if (!pool) {
    return;
  }
  const user = pool.getCurrentUser();
  user?.signOut();
  localStorage.removeItem(PERSISTENT_FLAG);
}

// Alleen voor tests: de adapter zit anders volledig achter de Cognito-SDK.
export const __testing = { SwitchableStorage };
